import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { addDaysToDate, addHours, customFormat } from 'utils/dateUtils'
import { extractErrorMessage } from 'utils/ErrorMessages'

import { selectActualDefaultLanguage, selectPartner } from 'containers/App/selectors'
import { CAMPAIGN_TYPES_VALUES } from 'containers/AwarenessCenter/constants'
import { MAP_DELIVERY_VALUE } from 'containers/AwarenessCenter/WizardCampaign/steps/constants'
import { DEFAULT_SIMULATION_PROPERTIES } from 'containers/AwarenessCenter/WizardCampaign/WizardSimulationCampaign/constants'
import {
  DEFAULT_COLLECTION_PERIOD,
  DEFAULT_TRAINING_PROPERTIES,
} from 'containers/AwarenessCenter/WizardCampaign/WizardTrainingCampaign/constants'
import { shareReset } from 'containers/Share/actions'
import { selectMessage, selectSelectedProfilesProperties } from 'containers/Share/selectors'

import { addToast } from '../../../Toasts/actions'

import {
  emergencyStopError,
  emergencyStopSuccess,
  getCampaignDataError,
  getCampaignDataSuccess,
  getReportParticipantsListError,
  getReportParticipantsListSuccess,
  getReportSummaryError,
  getReportSummarySuccess,
  resendTrainingError,
  saveDraftError,
  saveDraftSuccess,
  saveManualTrainingResultsError,
  saveManualTrainingResultsSuccess,
  shareCsvReportError,
} from './actions'
import {
  EMERGENCY_STOP_REQUEST_ASYNC,
  END_DATE_INTERVAL,
  GET_CAMPAIGN_DATA_ASYNC,
  GET_REPORT_PARTICIPANTS_LIST_ASYNC,
  GET_REPORT_SUMMARY_ASYNC,
  HOURS_TO_START_CAMPAIGN,
  RESEND_TRAINING_REQUEST_ASYNC,
  SAVE_DRAFT_ASYNC,
  SAVE_MANUAL_TRAINING_RESULTS_ASYNC,
  SEGMENT_FILTER_CHANGE,
  SHARE_ACTIONS,
  SHARE_CSV_REPORT_ASYNC,
} from './constants'
import {
  selectData,
  selectFilters,
  selectSegment,
  selectSelectionData,
  selectTrainingResultsModalStatus,
  selectTrainingResultsPostponedDate,
} from './selectors'

const getCampaignDraft = (campaignData) => {
  const CAMPAIGNS_MAP = {
    [CAMPAIGN_TYPES_VALUES.SIMULATION]: {
      endDate: customFormat(addDaysToDate(new Date(), END_DATE_INTERVAL), 'yyyy-MM-dd'),
      closeTime: `${customFormat(
        addDaysToDate(new Date(), END_DATE_INTERVAL + DEFAULT_COLLECTION_PERIOD),
        "yyyy-MM-dd'T'HH:mm"
      )}Z`,
      scenariosIds: campaignData.scenarios?.map((scenario) => scenario.id),
      landingPageId: campaignData.landing_page?.id,
      notifications: campaignData.notifications,
      ...DEFAULT_SIMULATION_PROPERTIES,
    },
    [CAMPAIGN_TYPES_VALUES.TRAINING]: {
      trainingCampaignData: {
        subject: campaignData.training_campaign_data?.subject,
        message: campaignData.training_campaign_data?.message,
        fromEmail: campaignData.training_campaign_data?.fromEmail,
      },
      closeTime: `${customFormat(addDaysToDate(new Date(), 21), "yyyy-MM-dd'T'HH:mm")}Z`,
      ...DEFAULT_TRAINING_PROPERTIES,
    },
  }

  return CAMPAIGNS_MAP[campaignData.flow_type]
}

const emergencyStopEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(EMERGENCY_STOP_REQUEST_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .emergencyStop(action.payload.id)
      .map((response) => emergencyStopSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(emergencyStopError, error)))
      .takeUntil(action$.ofType(EMERGENCY_STOP_REQUEST_ASYNC.CANCEL))
  )

const resendTrainingEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(RESEND_TRAINING_REQUEST_ASYNC.INITIATE).mergeMap((action) => {
    const selectionData = selectSelectionData(store.getState())

    return api.awarenessCenter
      .resendTraining(action.payload.id, {
        companyFilter: store.getState().partnerOptions.selectedCompany?.value,
        partner: !!selectPartner(store.getState()),
        search: selectFilters(store.getState()).search,
        segments: selectSegment(store.getState()),
        simulationResultTypes: selectFilters(store.getState()).simulationResultTypes,
        trainingResultTypes: selectFilters(store.getState()).trainingResultTypes,
        departments: selectFilters(store.getState()).departments,
        trainingScores: selectFilters(store.getState()).trainingResults,
        awarenessLevels: selectFilters(store.getState()).levels,
        scenarios: selectFilters(store.getState()).scenarios,
        selectionData: selectionData.allItems
          ? {
              allItems: true,
              excludeIds: selectionData.selectionIds,
            }
          : {
              allItems: false,
              includeIds: selectionData.selectionIds,
              excludeIds: [],
            },
      })
      .mergeMap(() => Observable.of(addToast({ message: 'Training emails resent successfully!' })))
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(resendTrainingError, error),
          addToast({ message: error.response?.message, type: 'error' })
        )
      )
      .takeUntil(action$.ofType(RESEND_TRAINING_REQUEST_ASYNC.CANCEL))
  })

const getReportParticipantsListEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_REPORT_PARTICIPANTS_LIST_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .getReportParticipantsList({
        companyFilter: store.getState().partnerOptions.selectedCompany?.value,
        partner: !!selectPartner(store.getState()),
        reportId: action.payload.params.reportId,
        page: action.payload.params.page,
        pageSize: action.payload.params.pageSize,
        search: selectFilters(store.getState()).search,
        simulationResultTypes: selectFilters(store.getState()).simulationResultTypes,
        trainingResultTypes: selectFilters(store.getState()).trainingResultTypes,
        departments: selectFilters(store.getState()).departments,
        trainingScores: selectFilters(store.getState()).trainingResults,
        awarenessLevels: selectFilters(store.getState()).levels,
        scenarios: selectFilters(store.getState()).scenarios,
        segments: selectSegment(store.getState()),
        trainingIds: selectFilters(store.getState()).trainingIds,
      })
      .switchMap((response) => Observable.of(getReportParticipantsListSuccess(response)))
      .catch((error) =>
        Observable.of(epicsErrorHandler(getReportParticipantsListError, error, [400]))
      )
      .takeUntil(action$.ofType(GET_REPORT_PARTICIPANTS_LIST_ASYNC.CANCEL))
  )

const getReportSummaryEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_REPORT_SUMMARY_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .getReportSummary(
        store.getState().partnerOptions.selectedCompany?.value,
        !!selectPartner(store.getState()),
        action.payload.reportId,
        selectSegment(store.getState())
      )
      .mergeMap((response) => Observable.of(getReportSummarySuccess(response)))
      .catch((error) => Observable.of(epicsErrorHandler(getReportSummaryError, error, [400])))
      .takeUntil(action$.ofType(GET_REPORT_SUMMARY_ASYNC.CANCEL))
  )

const createCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SAVE_DRAFT_ASYNC.INITIATE).mergeMap((action) => {
    const campaignData = selectData(store.getState())
    const now = new Date()
    const formattedDate = customFormat(now, 'yyMMdd_HHmmss')
    const campaignName = `Copy_Of_${campaignData.name}_${formattedDate}`
    const isAutomatedLevelBasedTraining = !campaignData.trainings?.length

    return api.wizardCampaign
      .saveCampaign(store.getState().partnerOptions.selectedCompany?.value, {
        defaultLocaleId:
          campaignData.locales[0]?.id || selectActualDefaultLanguage(store.getState()).id,
        participantsChoices: {
          Names: action.payload.selection,
        },
        flowType: campaignData.flow_type,
        name: campaignName,
        deliveryType: MAP_DELIVERY_VALUE[campaignData.delivery_type],
        scheduleTime: customFormat(
          addHours(new Date(), HOURS_TO_START_CAMPAIGN),
          "yyyy-MM-dd'T'HH:mm"
        ),
        trainingIds: isAutomatedLevelBasedTraining
          ? null
          : campaignData.trainings.map(({ id }) => id),
        sendCampaignFailedEmail: campaignData.sendCampaignFailedEmail,
        sendCampaignSummaryToCampaignReportRecipients:
          campaignData.sendCampaignSummaryToCampaignReportRecipients,
        sendCompletionEmail: campaignData.sendCompletionEmail,
        sendRemindersUntil: new Date(
          addDaysToDate(new Date(), END_DATE_INTERVAL + DEFAULT_COLLECTION_PERIOD)
        ).toISOString(),
        trainingReminders: campaignData.trainingReminders,
        ...getCampaignDraft(campaignData),
      })
      .map(({ response }) => saveDraftSuccess(response.id))
      .catch((error) => Observable.of(epicsErrorHandler(saveDraftError, error, [400])))
      .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
  })

const getCampaignDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_CAMPAIGN_DATA_ASYNC.INITIATE,
      RESEND_TRAINING_REQUEST_ASYNC.SUCCESS,
      EMERGENCY_STOP_REQUEST_ASYNC.SUCCESS,
      SEGMENT_FILTER_CHANGE
    )
    .mergeMap((action) => {
      const id = action.payload.id || action.payload.campaignId

      return api.wizardCampaign
        .getCampaign(store.getState().partnerOptions.selectedCompany?.value, id)
        .mergeMap((response) => Observable.of(getCampaignDataSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(getCampaignDataError, error, [400])))
        .takeUntil(action$.ofType(GET_CAMPAIGN_DATA_ASYNC.CANCEL))
    })

const shareCsvReportEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SHARE_CSV_REPORT_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .shareCsvReport(action.payload.id, {
        search: selectFilters(store.getState()).search,
        simulationResultTypes: selectFilters(store.getState()).simulationResultTypes,
        trainingResultTypes: selectFilters(store.getState()).trainingResultTypes,
        departments: selectFilters(store.getState()).departments,
        trainingScores: selectFilters(store.getState()).trainingResults,
        awarenessLevels: selectFilters(store.getState()).levels,
        scenarios: selectFilters(store.getState()).scenarios,
        segments: selectSegment(store.getState()),
        action: SHARE_ACTIONS.SHARE_WIA_EMAIL,
        shareData: {
          emails: selectSelectedProfilesProperties(store.getState()),
          message: selectMessage(store.getState()),
        },
      })
      .mergeMap(() =>
        Observable.of(addToast({ message: 'The Report has been sent' }), shareReset())
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(shareCsvReportError, error),
          addToast({ message: error.response?.message, type: 'error' })
        )
      )
      .takeUntil(action$.ofType(SHARE_CSV_REPORT_ASYNC.CANCEL))
  )

const saveManualTrainingResultsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SAVE_MANUAL_TRAINING_RESULTS_ASYNC.INITIATE).mergeMap((action) => {
    const selectionData = selectSelectionData(store.getState())

    return api.awarenessCenter
      .saveManualTrainingResults(action.payload.id, {
        search: selectFilters(store.getState()).search,
        simulationResultTypes: selectFilters(store.getState()).simulationResultTypes,
        trainingResultTypes: selectFilters(store.getState()).trainingResultTypes,
        departments: selectFilters(store.getState()).departments,
        trainingScores: selectFilters(store.getState()).trainingResults,
        awarenessLevels: selectFilters(store.getState()).levels,
        scenarios: selectFilters(store.getState()).scenarios,
        segments: selectSegment(store.getState()),
        status: selectTrainingResultsModalStatus(store.getState()),
        customPostponedDate: selectTrainingResultsPostponedDate(store.getState()),
        selectionData: selectionData.allItems
          ? {
              allItems: true,
              excludeIds: selectionData.selectionIds,
            }
          : {
              allItems: false,
              includeIds: selectionData.selectionIds,
              excludeIds: [],
            },
      })
      .mergeMap(({ response }) =>
        Observable.of(
          saveManualTrainingResultsSuccess(),
          addToast({
            message: `Training status for ${response.count} participants has been successfully changed. `,
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(saveManualTrainingResultsError, error),
          addToast({ message: extractErrorMessage(error.response?.message), type: 'error' })
        )
      )
      .takeUntil(action$.ofType(SHARE_CSV_REPORT_ASYNC.CANCEL))
  })

export default combineEpics(
  emergencyStopEpic,
  resendTrainingEpic,
  getReportParticipantsListEpic,
  getReportSummaryEpic,
  createCampaignEpic,
  getCampaignDataEpic,
  shareCsvReportEpic,
  saveManualTrainingResultsEpic
)
